import JsApi from '@tencent/tvf-jsapi';
import {
  getCookie,
  getLoginInfo,
  getYybLoginMiddlePage,
  handleError,
  handleSuccess,
  report,
  time33,
  getYybGetUserInfoApi,
  sleep,
  attaReport,
  cache,
} from '../helper';
import { ApiInterface, CacheType, Interceptor, InterceptorLoginConfig, LoginInfo, LoginType, WebLoginMode } from '../types';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Base } from './base';

// 端外统一cookie字段
const WEB_OPENID = 'yyb_openid';
const WEB_ACCESS_TOKEN = 'yyb_access_token';
const WEB_REFRESH_TOKEN = 'yyb_refresh_token';
const WEB_LOGIN_TYPE = 'yyb_logintype';
const WEB_APPID = 'yyb_appid';

// 端内 APP ID
const APP_ID_WX = 'wx3909f6add1206543';
const APP_ID_QQ = '1101070898';

// 清空端外 cookie
const clearYybCookies = () => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const yybCookies = [WEB_OPENID, WEB_ACCESS_TOKEN, WEB_REFRESH_TOKEN, WEB_LOGIN_TYPE, WEB_APPID];
  cookies.forEach((cookie) => {
    const [key] = cookie.split('=');

    if (yybCookies.includes(key)) {
      Cookies.remove(key, { domain: 'yyb.qq.com' });
    }
  });
};

// axios 实例
const axiosIns = axios.create({
  baseURL: '//m.yyb.qq.com',
  method: 'post',
  withCredentials: true,
  params: {
    g_tk: time33(getCookie(WEB_ACCESS_TOKEN)),
  },
});

/**
 * 应用宝
 */
export class YybApi extends Base implements ApiInterface {
  public needRefresh = false; // 是否需要续期，外部可以根据此属性进行事件监听并在续期后处理逻辑
  public refreshed = false; // 续期动作是否完成，无论是否成功
  public ifRefreshSuccess = false; // 续期是否成功

  /**
   * 是否在客户端内
   */
  private isInApp: boolean;

  public constructor({ isInApp }: { isInApp: boolean }) {
    super();
    this.isInApp = isInApp;

    // 端外续期
    if (!this.isInApp) {
      this.needRefresh = true;
      this.refresh();
    }
  }

  /**
   * 登录
   */
  @report('login')
  public async login() {
    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg } = await JsApi.invoke('actionLogin', { type: 'tv' });
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }
        return handleSuccess(true);
      } catch (error) {
        const { errCode } = error as any;
        if (errCode === 3003) {
          return handleError(-1, '当前版本过低，可能会导致登录失败，请升级至最新版本再试~');
        }
        // 如果取消登录会报错
        return handleError(-1, '取消登录');
      }
    }

    // 端外执行拦截器
    const chain: Interceptor<InterceptorLoginConfig>[] = [];
    let promise = Promise.resolve<InterceptorLoginConfig>({
      webLoginMode: undefined,
    });
    this.interceptors.login.forEach((interceptor) => {
      chain.push(interceptor);
    });
    while (chain.length > 0) {
      const { resolved, rejected } = chain.shift()!;
      promise = promise.then(resolved, rejected);
    }
    const { webLoginMode } = await promise;
    if (webLoginMode === WebLoginMode.CANCEL) {
      return handleError(-1, '取消登录');
    }

    // 端外跳中间页
    window.location.href = getYybLoginMiddlePage(webLoginMode);

    // 如果业务侧代码有 location.reload()，那么等待两秒返回以保证 href 正确跳转
    await sleep(2000);

    // IOS 和部分安卓在 location.href 跳转页面后，此时如果没有登录而是直接回退页面时不会刷新页面
    // 因此状态码返回 -1 为了防止 JS 继续执行时误判登录成功
    return handleError(-1, '取消登录');
  }

  /**
   * 退出登录
   */
  @report('logout')
  public async logout(needReload = true) {
    // 未登录不允许调用
    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '当前未登录，无法退出登录');
    }

    // 端内
    if (this.isInApp) {
      const { errCode, errMsg } = await JsApi.invoke('logout');
      if (errCode !== 0) {
        return handleError(errCode, errMsg);
      }
      return handleSuccess(true);
    }

    // 端外清空 cookie
    clearYybCookies();
    if (needReload) {
      location.reload();
    }
    return handleSuccess(true);
  }

  /**
   * 是否登录
   */
  @report('isLogin')
  public async isLogin() {
    let openid = '';
    let accessToken = '';
    if (this.isInApp) {
      openid = getCookie('mobileqopenid') || getCookie('openid');
      accessToken = getCookie('mobileqaccesstoken') || getCookie('accesstoken');
    } else {
      openid = getCookie(WEB_OPENID);
      accessToken = getCookie(WEB_ACCESS_TOKEN);
    }

    // openid 和 accessToken 同时存在时认为已登录
    // 因此如果登录了，但是已过期，则当做未登录
    return handleSuccess(!!(openid && accessToken));
  }

  /**
   * 获取登录信息，cookie
   */
  @report('getLoginInfo')
  public async getLoginInfo() {
    return getLoginInfo({
      isInApp: this.isInApp,
      loginInfoHandler: (_loginInfo: LoginInfo, appCookies: Record<string, string>) => {
        const loginInfo = _loginInfo;

        if (this.isInApp) {
          // 写死
          loginInfo.appid = loginInfo.type === LoginType.wx ? APP_ID_WX : APP_ID_QQ;
          loginInfo.openid = getCookie('mobileqopenid', appCookies) || getCookie('openid', appCookies);
          loginInfo.accessToken = getCookie('mobileqaccesstoken', appCookies) || getCookie('accesstoken', appCookies);
        } else {
          loginInfo.appid = getCookie(WEB_APPID);
          loginInfo.openid = getCookie(WEB_OPENID);
          loginInfo.accessToken = getCookie(WEB_ACCESS_TOKEN);
          loginInfo.refreshToken = getCookie(WEB_REFRESH_TOKEN);
        }
      },
    });
  }

  /**
   * 获取用户信息，头像昵称
   */
  @report('getUserInfo')
  @cache(CacheType.USER_INFO)
  public async getUserInfo() {
    const userInfo = {
      nickname: '',
      avatar: '',
    };

    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '未登录，无法获取用户信息');
    }

    // HTTP 请求获取，无 JSB 调用
    const res = await axios.get(getYybGetUserInfoApi(), {
      withCredentials: true,
    });
    const { code, msg, user_info: info } = res.data;
    if (code !== 0) {
      return handleError(code, msg);
    }
    userInfo.nickname = info.nick;
    userInfo.avatar = info.avatar;

    return handleSuccess(userInfo);
  }

  /**
   * 端外续期逻辑
   */
  private async refresh() {
    // 如果不存在 openid cookie 则不续期
    const openid = getCookie(WEB_OPENID);
    if (!openid) {
      console.log('[iwan-login] 不存在 openid 不续期');
      this.finishRefresh(false);
      return;
    }

    // 是否过期
    const isExpired = await this.isExpired();
    if (!isExpired) {
      this.finishRefresh(false);
      return;
    }

    // 续期
    const { data } = await axiosIns({
      url: '/out-login/refresh',
      data: {
        loginType: getCookie(WEB_LOGIN_TYPE),
        openID: getCookie(WEB_OPENID),
        accessToken: getCookie(WEB_ACCESS_TOKEN),
        refreshToken: getCookie(WEB_REFRESH_TOKEN),
      },
    }) as { data: any };
    if (data.code === 0) {
      console.log('[iwan-login] 登录态续期成功: ', data);
      this.finishRefresh(true);
      return;
    }
    attaReport({
      api: 'refresh',
      errCode: -1,
      errMsg: data.msg,
    });
    console.error('[iwan-login] 登录态过期, 续期失败, 请手动登录: ', data.msg);
    this.finishRefresh(false);
  }

  /**
   * 端外检查登录态是否过期
   */
  private async isExpired() {
    const { data } = await axiosIns({
      url: '/out-login/auth',
      data: {
        loginType: getCookie(WEB_LOGIN_TYPE),
        openID: getCookie(WEB_OPENID),
        accessToken: getCookie(WEB_ACCESS_TOKEN),
        refreshToken: getCookie(WEB_REFRESH_TOKEN),
      },
    }) as { data: any };

    return data.code !== 0;
  }

  private finishRefresh(ifSuccess: boolean) {
    this.emit('refresh', ifSuccess);
    this.refreshed = true;
    this.ifRefreshSuccess = ifSuccess;
  }
}
